import DatePicker from "@/components/DatePicker";
import DateRangePicker from "@/components/DateRangePicker";
import DebouncedInput from "@/components/DebouncedInput";
import SelectFilter from "@/components/SelectFilter";
import YearMonthPicker from "@/components/YearMonthPicker";
import { Box, SimpleGrid } from "@chakra-ui/react";
import { Search } from "lucide-react";
import { Dispatch, SetStateAction, useCallback } from "react";
import {
  mapCreationMethod,
  mapErpSyncStatus,
  mapPaymentMethod,
  mapStatus,
} from "../mappers/invoice.mapper";
import { FiltersType } from "../types/filters.type";

type Props = {
  queryFilters: FiltersType;
  setQueryFilters: Dispatch<SetStateAction<FiltersType>>;
};

const Filters = ({ queryFilters, setQueryFilters }: Props): JSX.Element => {
  const handleSearchByDateRange = useCallback(
    (dates: [string, string] | undefined) => {
      setQueryFilters((prev) => ({ ...prev, dateRange: dates }));
    },
    [setQueryFilters]
  );

  const handleIdChange = useCallback(
    (value: string) => setQueryFilters((prev) => ({ ...prev, id: value })),
    [setQueryFilters]
  );

  const handleContractChange = useCallback(
    (value: string) =>
      setQueryFilters((prev) => ({ ...prev, contract: value })),
    [setQueryFilters]
  );

  const handleDocumentChange = useCallback(
    (value: string) =>
      setQueryFilters((prev) => ({
        ...prev,
        customerDocumentNumber: value,
      })),
    [setQueryFilters]
  );

  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 5 }} gap="1rem" mb="1rem">
      <Box position="relative">
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
        >
          <Search size={16} />
        </Box>
        <DebouncedInput
          size="sm"
          placeholder="ID"
          _placeholder={{ color: "#5D5C60" }}
          value={queryFilters.id}
          onDebouncedChange={handleIdChange}
        />
      </Box>
      <Box position="relative">
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
        >
          <Search size={16} />
        </Box>
        <DebouncedInput
          size="sm"
          placeholder="Contrato"
          _placeholder={{ color: "#5D5C60" }}
          value={queryFilters.contract}
          onDebouncedChange={handleContractChange}
        />
      </Box>
      <Box position="relative">
        <Box
          position="absolute"
          right={3}
          top="50%"
          transform="translateY(-50%)"
          zIndex={1}
        >
          <Search size={16} />
        </Box>
        <DebouncedInput
          size="sm"
          placeholder="Documento"
          _placeholder={{ color: "#5D5C60" }}
          value={queryFilters.customerDocumentNumber}
          onDebouncedChange={handleDocumentChange}
        />
      </Box>
      <SelectFilter
        isClearable
        options={Object.keys(mapCreationMethod)}
        selectedOption={queryFilters.creationMethod}
        onSelect={(option) =>
          setQueryFilters((prev) => ({
            ...prev,
            creationMethod: option,
          }))
        }
        placeholder="Método de Creación"
        width="100%"
      />
      <YearMonthPicker
        setValue={(period) => {
          setQueryFilters((prev) => ({ ...prev, rentalPeriod: period }));
        }}
        placeholder="Mes Facturado"
      />
      <SelectFilter
        options={Object.keys(mapStatus)}
        selectedOption={queryFilters.status}
        onSelect={(option) =>
          setQueryFilters((prev) => ({ ...prev, status: option }))
        }
        placeholder="Estado"
        width="100%"
      />
      <SelectFilter
        options={Object.keys(mapPaymentMethod)}
        selectedOption={queryFilters.paymentMethod}
        onSelect={(option) =>
          setQueryFilters((prev) => ({ ...prev, paymentMethod: option }))
        }
        placeholder="Método de Pago"
        width="100%"
      />
      <DatePicker
        minDate={new Date(Date.now() - 1000 * 60 * 60 * 24)}
        placeholder="Próximo Envío"
        value={queryFilters.sendDate}
        setValue={(date) =>
          setQueryFilters((prev) => ({ ...prev, sendDate: date }))
        }
      />
      <DateRangePicker
        maxDate={new Date()}
        placeholder="Fecha de Pago"
        values={queryFilters.dateRange}
        setValues={handleSearchByDateRange}
      />
      <SelectFilter
        options={Object.keys(mapErpSyncStatus)}
        selectedOption={queryFilters.erpSyncStatus}
        onSelect={(option) =>
          setQueryFilters((prev) => ({ ...prev, erpSyncStatus: option }))
        }
        placeholder="Contabilización ERP"
        width="100%"
      />
    </SimpleGrid>
  );
};

export default Filters;
